export interface UploadFileFingerprint {
  name: string;
  size: number;
  lastModified: number;
  type: string;
}

export interface PersistedUploadedPart {
  partNumber: number;
  etag: string;
}

export interface PersistedMultipartUploadState {
  localUploadId: string;
  scope: string;
  fileFingerprint: UploadFileFingerprint;
  fileId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  totalParts: number;
  uploadedParts: PersistedUploadedPart[];
  expiresAt: string;
  status: string;
  context?: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface PersistedMultipartUploadStatusPart {
  partNumber: number;
  etag: string;
}

export interface PersistedMultipartUploadStatusSnapshot {
  fileId: string;
  uploadStatus: string;
  uploadedParts?: PersistedMultipartUploadStatusPart[];
  totalParts?: number;
  expiresAt?: string;
}

const storageKeyPrefix = "Chat@MultipartUpload:";

const getStorageKey = (scope: string) => `${storageKeyPrefix}${scope}`;

export const getPersistedMultipartUpload = (
  scope: string
): PersistedMultipartUploadState | null => {
  const serialized = localStorage.getItem(getStorageKey(scope));

  if (!serialized) {
    return null;
  }

  try {
    return JSON.parse(serialized) as PersistedMultipartUploadState;
  } catch {
    localStorage.removeItem(getStorageKey(scope));
    return null;
  }
};

export const setPersistedMultipartUpload = (
  scope: string,
  value: PersistedMultipartUploadState
) => {
  localStorage.setItem(getStorageKey(scope), JSON.stringify(value));
};

export const clearPersistedMultipartUpload = (scope: string) => {
  localStorage.removeItem(getStorageKey(scope));
};

const sortUploadedParts = (parts: PersistedMultipartUploadState["uploadedParts"]) =>
  [...parts].sort((left, right) => left.partNumber - right.partNumber);

export const mergePersistedUploadedParts = (
  localParts: PersistedMultipartUploadState["uploadedParts"],
  remoteParts?: PersistedMultipartUploadState["uploadedParts"] | null
) => {
  const merged = new Map<number, string>();

  localParts.forEach((part) => {
    merged.set(part.partNumber, part.etag);
  });
  (remoteParts ?? []).forEach((part) => {
    merged.set(part.partNumber, part.etag);
  });

  return sortUploadedParts(
    Array.from(merged.entries()).map(([partNumber, etag]) => ({ partNumber, etag }))
  );
};

export const reconcilePersistedMultipartSession = (
  session: PersistedMultipartUploadState,
  remoteStatus?: PersistedMultipartUploadStatusSnapshot | null
): PersistedMultipartUploadState | null => {
  if (!remoteStatus) {
    return session;
  }

  const normalizedStatus = (remoteStatus.uploadStatus ?? session.status ?? "").toUpperCase();
  if (["COMPLETED", "ABORTED", "EXPIRED", "FAILED"].includes(normalizedStatus)) {
    return null;
  }

  return {
    ...session,
    totalParts:
      remoteStatus.totalParts && remoteStatus.totalParts > 0
        ? remoteStatus.totalParts
        : session.totalParts,
    uploadedParts: mergePersistedUploadedParts(session.uploadedParts, remoteStatus.uploadedParts),
    expiresAt: remoteStatus.expiresAt ?? session.expiresAt,
    status: normalizedStatus || session.status,
    updatedAt: new Date().toISOString(),
  };
};
